/**
 * S4 제어 평면 계약(C-07 · Bearer · 기준 VITE_FIELD_API) — 전시관·반응(FS-03) · 리플레이·도전 기록(FS-04 · FS-14).
 *   GET  /api/courses/{c}/gallery → {items:[GalleryItem]}                  (공개 범위는 서버가 거른다 — 비공개 제외)
 *   PUT  /api/courses/{c}/snapshots/{id}/reaction {stickerKey}           (422 FE-305 카탈로그 밖 · 409 내 작품)
 *   DELETE /api/courses/{c}/snapshots/{id}/reaction
 *   GET  /api/catalog?type=sticker → {items:[{type,key,version,i18n,payload}]}
 *   POST /api/courses/{c}/replays  multipart(meta · file · track) → 201 {replayId, expiresAt} · 413 너무 큼
 *   GET  /api/courses/{c}/replays?modeId=&presetKey=&limit= → {items:[…]}   (시간 없음 — SR-LCL-03)
 *   GET  /api/courses/{c}/replays/{id} · …/file · …/track                  (410 만료)
 *   GET  /api/courses/{c}/replays/{id}/bundles/{snapshotId} → RunBundle     (P3-S6 룸 권위 리플레이의 명부 묶음 — 교류 룸 상대 반 기계 포함 · 리플레이와 같은 권한)
 *   PATCH /api/courses/{c}/replays/{id} {hashOk} → 204(재생 검증 결과)
 *   POST /api/courses/{c}/attempts → 201 {attemptId, verification}(4.6 미션 — missionApi) · GET …/attempts/mine
 * 오류(승인 2026-09-25 · SRS 부록 B): 409 FE-307 내 작품 반응 · 413 FE-503 리플레이 512KB 초과 · 410 FE-504 리플레이 만료.
 * 별명(nickname)은 서버가 준다(자동 별명 · 교사 별명표) — 남에게 보이는 이름은 세션 displayName(LTI 실명일 수 있음)을 쓰지 않는다.
 * 반응 개수(reactionCount)는 내 작품·교사에게만 온다 — 셸은 남의 카드에 개수를 그리지 않고, 반응으로 정렬하지 않는다(SR-SOC-02).
 */
import { useMemo } from "react";
import type { ReplayExport, ReplayMeta, RunBundle } from "@picnic-field/local-host/protocol";
import type { Appearance } from "@picnic-field/renderer/appearance";
import { useSession, useSessionManager } from "../../store/session/SessionProvider";
import { ApiError } from "../http/errors";
import type { LessonRef } from "./fieldApi";
import type { BytesRequester } from "../../types/api";
import { readAttemptPosted } from "./missionApi";
import type { AttemptPosted } from "./missionApi";

export interface GalleryItem {
  snapshotId: string;
  /** 별명(교사 별명표 또는 자동 별명) — 실명 없음 */
  nickname: string;
  title: string;
  modelSlug: string;
  glbUrl: string | null;
  /** S5 선택 — 렌더 LOD 주소(없으면 glbUrl). 정지 전시는 L2, 작동은 L1 */
  lodUrls?: { l1?: string | null; l2?: string | null } | null;
  appearance: Appearance | null;
  tags: string[];
  static: boolean;
  lesson?: LessonRef | null;
  mine: boolean;
  /** 내가 붙인 스티커 키(없으면 null) */
  myReaction: string | null;
  /** 내 작품(또는 교사)일 때만 온다 */
  reactionCount?: number;
}

export interface Sticker {
  key: string;
  emoji: string;
  /** 읽어 줄 이름(카탈로그 i18n) */
  name: string;
}

export interface ReplayListItem {
  replayId: string;
  nickname: string;
  completed: boolean;
  snapshotId: string;
  presetKey: string;
  createdAt: string;
  mine: boolean;
}

export interface ReplayInfo {
  replayId: string;
  nickname: string;
  completed: boolean;
  presetKey: string;
  modeId: string;
  snapshotIds: string[];
  meta?: ReplayMeta | null;
  hashOk: boolean;
  expiresAt: string;
  mine: boolean;
}

export interface AttemptInput {
  snapshotId: string;
  modeId: string;
  presetKey: string;
  outcome: "complete" | "incomplete";
  durationMs: number;
  tries: number;
  failPattern?: string | null;
  replayId?: string | null;
}

export interface SocialApi {
  gallery(): Promise<GalleryItem[]>;
  /** GET /api/courses/{c}/me/nickname → {nickname} — 남에게 보이는 내 별명(첫 올리기 전에도). 못 받으면 null */
  myNickname(): Promise<string | null>;
  setReaction(snapshotId: string, stickerKey: string): Promise<void>;
  clearReaction(snapshotId: string): Promise<void>;
  stickers(): Promise<Sticker[]>;
  uploadReplay(r: ReplayExport): Promise<{ replayId: string; expiresAt: string | null }>;
  replays(q: { modeId: string; presetKey: string; limit?: number }): Promise<ReplayListItem[]>;
  /** 410 이면 {@link isExpired} */
  replay(id: string): Promise<ReplayInfo>;
  replayFile(id: string): Promise<Uint8Array>;
  /**
   * 룸 권위 리플레이에 든 스냅샷의 실행 묶음(P3-S6) — 교류 룸의 상대 반 기계는 코스 run-bundle 경로로 못 받으므로(404) 리플레이 경로로.
   */
  replayBundle(id: string, snapshotId: string): Promise<RunBundle>;
  /** 위치 트랙이 없으면(404) null */
  replayTrack(id: string): Promise<Uint8Array | null>;
  /**
   * SR-REC-02 권위 리플레이 서버 재생 조각(server-track@1 · ≤30초) — GET …/replays/{id}/server-track?from=&to=.
   * 워커가 바쁘거나 없으면 503(HTTP-503) — 부른 쪽은 입력 재생을 이어 간다.
   */
  replayServerTrack(id: string, from: number, to: number): Promise<Uint8Array>;
  /** 201 {attemptId, verification} — 4.6 §4.3 미션 판이면 verification = 확인 대기(서버 재생 검증) */
  postAttempt(a: AttemptInput): Promise<AttemptPosted>;
  /** 재생 검증 결과(고스트 · 뷰어) — PATCH …/replays/{id} {hashOk}. 실패해도 조용히 */
  reportHash(id: string, hashOk: boolean): Promise<void>;
}

/** 리플레이 보관 기간(180일)이 지났다(410 FE-504) */
export const isExpired = (e: unknown) =>
  e instanceof ApiError && (e.status === 410 || e.code === "FE-504");
/** 리플레이가 너무 크다(413 FE-503 · 512KB) — 다시 올려도 같다 */
export const isTooLarge = (e: unknown) =>
  e instanceof ApiError && (e.status === 413 || e.code === "FE-503");

const listOf = <T>(v: unknown, ...keys: string[]): T[] => {
  if (Array.isArray(v)) return v as T[];
  const o = (v ?? {}) as Record<string, unknown>;
  for (const k of keys) if (Array.isArray(o[k])) return o[k] as T[];
  return [];
};

/** 카탈로그에 이모지가 없을 때(초기 시드) 아는 키만 */
const KNOWN_EMOJI: Record<string, string> = {
  star: "⭐",
  strong: "💪",
  love: "😍",
  wow: "😮",
  clap: "👏",
  idea: "💡",
  heart: "❤️",
  smile: "😊",
};

/** GET /api/catalog?type=sticker → 스티커(카탈로그 순서 그대로 — 인기순 정렬 없음) */
export function readStickers(v: unknown, locale: "ko" | "en" = "ko"): Sticker[] {
  const out: Sticker[] = [];
  for (const raw of listOf<Record<string, unknown>>(v, "items")) {
    const key = typeof raw.key === "string" ? raw.key : null;
    if (!key) continue;
    const payload = (raw.payload ?? raw.data ?? {}) as Record<string, unknown>;
    const emoji =
      typeof payload.emoji === "string" && payload.emoji
        ? payload.emoji
        : (KNOWN_EMOJI[key] ?? null);
    if (!emoji) continue;
    const i18n = (raw.i18n ?? {}) as Record<string, unknown>;
    const name =
      (typeof i18n[locale] === "string" && (i18n[locale] as string)) ||
      (typeof i18n.ko === "string" && (i18n.ko as string)) ||
      key;
    if (!out.some((s) => s.key === key)) out.push({ key, emoji, name });
  }
  return out;
}

const str = (v: unknown, d = "") => (typeof v === "string" ? v : d);

/**
 * 계약 밖 필드는 버리고(점수·순위 같은 것이 섞여 와도 그리지 않게) 필요한 것만.
 * 순서는 서버 그대로(내 작품 먼저 → 별명 → id — 순위 아님). 반응순으로 다시 정렬하지 않는다(SR-SOC-02).
 */
export function readGallery(v: unknown): GalleryItem[] {
  return listOf<Record<string, unknown>>(v, "items")
    .filter((o) => typeof o?.snapshotId === "string")
    .map((o) => {
      const item: GalleryItem = {
        snapshotId: o.snapshotId as string,
        nickname: str(o.nickname),
        title: str(o.title),
        modelSlug: str(o.modelSlug),
        glbUrl: typeof o.glbUrl === "string" ? o.glbUrl : null,
        appearance: (o.appearance as Appearance | null) ?? null,
        tags: Array.isArray(o.tags)
          ? (o.tags as unknown[]).filter((t): t is string => typeof t === "string")
          : [],
        static: o.static === true,
        lesson: (o.lesson as LessonRef | null) ?? null,
        mine: o.mine === true,
        myReaction: typeof o.myReaction === "string" ? o.myReaction : null,
      };
      // 개수는 내 작품일 때만 받는다(교사 응답을 학생 화면에 쓰지 않게)
      if (item.mine && typeof o.reactionCount === "number") item.reactionCount = o.reactionCount;
      return item;
    })
    .slice(0, 30);
}

export function readReplayList(v: unknown): ReplayListItem[] {
  return listOf<Record<string, unknown>>(v, "items")
    .filter((o) => typeof o?.replayId === "string")
    .map((o) => ({
      replayId: o.replayId as string,
      nickname: str(o.nickname),
      completed: o.completed === true,
      snapshotId: str(o.snapshotId),
      presetKey: str(o.presetKey),
      createdAt: str(o.createdAt),
      mine: o.mine === true,
    }));
}

export function createSocialApi(
  request: BytesRequester,
  courseId: string,
  locale: "ko" | "en" = "ko",
): SocialApi {
  const c = `/api/courses/${encodeURIComponent(courseId)}`;
  const snap = (id: string) => `${c}/snapshots/${encodeURIComponent(id)}`;
  const rp = (id: string) => `${c}/replays/${encodeURIComponent(id)}`;
  return {
    async gallery() {
      return readGallery(await request<unknown>(`${c}/gallery`));
    },
    async myNickname() {
      try {
        const v = await request<{ nickname?: unknown } | null>(`${c}/me/nickname`);
        return typeof v?.nickname === "string" && v.nickname ? v.nickname : null;
      } catch {
        return null;
      }
    },
    async setReaction(id, stickerKey) {
      await request(`${snap(id)}/reaction`, {
        method: "PUT",
        body: JSON.stringify({ stickerKey }),
      });
    },
    async clearReaction(id) {
      await request(`${snap(id)}/reaction`, { method: "DELETE" });
    },
    async stickers() {
      return readStickers(await request<unknown>("/api/catalog?type=sticker"), locale);
    },
    async uploadReplay(r) {
      const fd = new FormData();
      fd.append(
        "meta",
        new Blob([JSON.stringify(r.meta)], { type: "application/json" }),
        "meta.json",
      );
      fd.append(
        "file",
        new Blob([r.file as BlobPart], { type: "application/octet-stream" }),
        "replay.bin",
      );
      fd.append(
        "track",
        new Blob([r.positionTrack as BlobPart], { type: "application/octet-stream" }),
        "track.bin",
      );
      const v = await request<{ replayId?: unknown; expiresAt?: unknown }>(`${c}/replays`, {
        method: "POST",
        body: fd,
      });
      if (typeof v?.replayId !== "string") throw new ApiError("NET-SERVER", 201, "replayId 없음");
      return {
        replayId: v.replayId,
        expiresAt: typeof v.expiresAt === "string" ? v.expiresAt : null,
      };
    },
    async replays({ modeId, presetKey, limit = 12 }) {
      const q = new URLSearchParams({ modeId, presetKey, limit: String(limit) });
      return readReplayList(await request<unknown>(`${c}/replays?${q}`));
    },
    async replay(id) {
      const o = await request<Record<string, unknown>>(rp(id));
      return {
        replayId: str(o.replayId, id),
        nickname: str(o.nickname),
        completed: o.completed === true,
        presetKey: str(o.presetKey),
        modeId: str(o.modeId),
        snapshotIds: Array.isArray(o.snapshotIds)
          ? (o.snapshotIds as unknown[]).filter((s): s is string => typeof s === "string")
          : [],
        meta: (o.meta as ReplayMeta | null) ?? null,
        hashOk: o.hashOk !== false,
        expiresAt: str(o.expiresAt),
        mine: o.mine === true,
      };
    },
    replayFile: (id) => request<Uint8Array>(`${rp(id)}/file`, { as: "bytes" }),
    replayBundle: (id, snapshotId) =>
      request<RunBundle>(`${rp(id)}/bundles/${encodeURIComponent(snapshotId)}`),
    async replayTrack(id) {
      try {
        return await request<Uint8Array>(`${rp(id)}/track`, { as: "bytes" });
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    replayServerTrack: (id, from, to) =>
      request<Uint8Array>(
        `${rp(id)}/server-track?from=${Math.max(0, Math.floor(from))}&to=${Math.max(0, Math.floor(to))}`,
        { as: "bytes" },
      ),
    async reportHash(id, hashOk) {
      try {
        await request(rp(id), { method: "PATCH", body: JSON.stringify({ hashOk }) });
      } catch {
        /* 검증 보고는 재생을 막지 않는다 */
      }
    },
    async postAttempt(a) {
      const body: Record<string, unknown> = { ...a };
      if (a.failPattern == null) delete body.failPattern;
      if (a.replayId == null) delete body.replayId;
      return readAttemptPosted(
        await request<unknown>(`${c}/attempts`, { method: "POST", body: JSON.stringify(body) }),
      );
    },
  };
}

export function useSocialApi(): SocialApi | null {
  const manager = useSessionManager();
  const state = useSession();
  const courseId = state.status === "ready" ? state.session.courseId : null;
  const locale = state.status === "ready" && state.session.locale.startsWith("en") ? "en" : "ko";
  return useMemo(
    () => (courseId ? createSocialApi(manager.request, courseId, locale) : null),
    [manager, courseId, locale],
  );
}
